import { weekMap } from 'constants/course/batchScheduledCourse.constant';
import { TBatchClaimScheduledMethod, TCourseFrequency } from 'types/course';
import { getWeeklyCourseDeviceTime } from 'utils/formatTime';

/**
 * 安全解析通知的 `contentObject`。
 *
 * 各種 `messageType` 的 `contentObject` 形狀並不一致，新增的類型也不保證是課程物件
 * —— 可能是別的結構，也可能是空字串。
 *
 * 原本四處都是裸的 `JSON.parse`，其中 `NotificationService` 那一處在 Provider 的
 * effect 裡：只要有任何一筆解析失敗就會 throw，後面的 `setAllNotifications` 不會執行，
 * **鈴鐺與通知頁會一起變成空的**，而且不是只有那一筆受影響。
 *
 * 呼叫端自己標註期望的形狀，因為不同欄位來自不同類型的通知，沒有共通型別可用。
 */
export const parseNotificationContent = <T>(
  contentObject?: string,
): T | undefined => {
  if (!contentObject) return undefined;

  try {
    return JSON.parse(contentObject) as T;
  } catch {
    return undefined;
  }
};

/**
 * 搶連續課通知的頻率描述，依排課方式分兩種寫法：
 *
 * - 可選時間：每週 2 次，每次 50 分鐘
 * - 指定時間：每週一 25 分鐘、每週三 50 分鐘
 *
 * 可選時間的一「次」是一天（＝搶課時要挑的一組），不是一堂課 —— 所以「每次 O 分鐘」要算
 * 那一天實際的授課時間，也就是 `minute × sequentialCourseCount`。
 *
 * 指定時間不能套用同一句：每個時段的堂數（`credit`）可以不同，「每次 O 分鐘」沒有單一數值，
 * 所以改成逐一列出星期幾與那天的時長（與排課請求頁的卡片一致，那邊也不顯示「N 次｜O 分鐘」）。
 *
 * 舊通知沒有 `scheduledMethod`，一律走可選時間的寫法，維持原本的顯示。
 */
export const formatClaimBatchFrequencyText = ({
  scheduledMethod,
  courseFrequency,
  recurringPeriod,
  weeklyLessonDays,
  minute,
  sequentialCourseCount,
}: {
  scheduledMethod?: TBatchClaimScheduledMethod;
  courseFrequency?: TCourseFrequency[];
  recurringPeriod?: number;
  weeklyLessonDays?: number;
  minute?: number;
  sequentialCourseCount?: number;
}) => {
  const recurringText = (recurringPeriod ?? 1) > 1 ? '隔週' : '每週';

  if (scheduledMethod === 'specifiedTime') {
    return (courseFrequency ?? [])
      .map(frequency => {
        // 星期幾要換算成老師所在時區，跨日時會跟著變
        const { dayOfWeek } = getWeeklyCourseDeviceTime(
          frequency.dayOfWeek,
          frequency.startTime,
        );
        const minutes =
          (frequency.minute ?? minute ?? 0) * Math.max(1, frequency.credit);

        return `${recurringText}${
          weekMap[dayOfWeek as keyof typeof weekMap]
        } ${minutes} 分鐘`;
      })
      .join('、');
  }

  const minutesPerTime =
    (minute ?? 0) * Math.max(1, sequentialCourseCount ?? 1);

  return `${recurringText} ${
    weeklyLessonDays ?? 0
  } 次，每次 ${minutesPerTime} 分鐘`;
};
